/**
 * SB2-CC-01 — Role-matrix context (read-side primitive in `@aeriosiq/ui`).
 *
 * `@aeriosiq/ui` deliberately does not depend on
 * `@aeriosiq/auth-client` or `@tanstack/react-query` (kept dep-light).
 * The *storage* of the loaded matrix lives here as a plain React context;
 * the *populator* of that context lives at
 * `apps/web/src/features/auth/RoleContext.tsx` and is responsible for
 * fetching the matrix via TanStack Query and reading the caller's role
 * from the auth store.
 *
 * Consumers (the `useCanEditResource` / `useCanEditResourceFor` hooks
 * exported alongside, plus app code that needs the raw matrix) read
 * from this context. When no provider has been mounted (e.g. during
 * pre-render or in a test that hasn't seeded one), the hooks return a
 * **safe-default-deny** state: `role === null`, `matrix === null`,
 * `isLoading === true`. The `useCanEditResource` hook then resolves to
 * `false`, which renders read-only UI — the brief's "no flash of
 * editable UI before the matrix lands" requirement.
 */
import { createContext, useContext, type ReactNode } from "react";
import type { AuthComponents } from "@aeriosiq/types";

export type RoleMatrix = AuthComponents["schemas"]["RoleMatrix"];
export type PermissionKey = AuthComponents["schemas"]["PermissionKey"];
export type RoleMatrixCell = AuthComponents["schemas"]["RoleMatrixCell"];
export type Role = AuthComponents["schemas"]["Role"];

export interface RoleMatrixContextValue {
  /** Caller's canonical role; `null` while the session is still bootstrapping. */
  role: Role | null;
  /** Caller's tenant slug (e.g. `aero-enterprise`); `null` while bootstrapping. */
  tenantSlug: string | null;
  /** The full matrix served by `GET /api/auth/v1/role-matrix`; `null` while loading. */
  matrix: RoleMatrix | null;
  /** True until the first non-error response of `/role-matrix` lands. */
  isLoading: boolean;
}

const DEFAULT_VALUE: RoleMatrixContextValue = {
  role: null,
  tenantSlug: null,
  matrix: null,
  isLoading: true,
};

export const RoleMatrixContext =
  createContext<RoleMatrixContextValue>(DEFAULT_VALUE);

export interface RoleMatrixContextProviderProps {
  value: RoleMatrixContextValue;
  children: ReactNode;
}

/**
 * Thin wrapper around `RoleMatrixContext.Provider` so app code can
 * `import { RoleMatrixContextProvider }` instead of importing the raw
 * `Context.Provider` shape (which couples to the React-internal type).
 */
export function RoleMatrixContextProvider({
  value,
  children,
}: RoleMatrixContextProviderProps) {
  return (
    <RoleMatrixContext.Provider value={value}>
      {children}
    </RoleMatrixContext.Provider>
  );
}

export function useRoleMatrixContext(): RoleMatrixContextValue {
  return useContext(RoleMatrixContext);
}
